import { type DownloadPayoutVoucherV2Params } from '@/types';
import { request } from '@umijs/max';
import Big from 'big.js';
import Cookies from 'js-cookie';
import { pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

export function getQueryString(name: string) {
  const regex = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
  const result = window.location.search.slice(1).match(regex);
  if (result != null) return decodeURI(result[2]);
  return null;
}

/** 登录/登出后的目标路径：不应为 /exception/*，否则会「再登录仍进 403」 */
export function getSafePostAuthPathname(pathname: string): string | undefined {
  if (!pathname) return undefined;
  if (pathname.startsWith('/exception/')) return undefined;
  return pathname;
}

/** 查询参数 redirect（可含 ?query）；路径落在 /exception/ 时丢弃 */
export function getSafeRedirectFromSearchParam(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const path = raw.split('?')[0];
  if (path.startsWith('/exception/')) return undefined;
  return raw;
}

export function setFromChannel(channel: string | null | undefined) {
  if (channel === undefined || channel == null) {
    Cookies.remove('panasia-portal-pro-from-channel');
    return;
  }
  // 30 days to expire
  const expireTime = new Date(new Date().getTime() + 30 * 24 * 3600 * 1000);
  Cookies.set('panasia-portal-pro-from-channel', channel, {
    expires: expireTime,
  });
}

export function isFromChannel() {
  return Cookies.get('panasia-portal-pro-from-channel') != null;
}

export function getLoginFailedTime() {
  let time: number = 0;
  if (Cookies.get('panasia-portal-pro-login-failed')) {
    time = Number(Cookies.get('panasia-portal-pro-login-failed'));
  }
  time += 1;
  // 5 mins to expire
  const expireTime = new Date(new Date().getTime() + 300 * 1000);
  Cookies.set('panasia-portal-pro-login-failed', String(time), {
    expires: expireTime,
  });
  return time;
}

/** 与 xPredictorWeb 一致：持久化 token，供 Authorization: Bearer 使用；服务端 JWT 有效期 72h */
const COMPLIANCE_AUTH_TOKEN_KEY = 'massPayment-compliance-auth-token';
const TOKEN_COOKIE_EXPIRES_MS = 72 * 3600 * 1000;

export function setToken(token: string | null | undefined) {
  if (token === undefined || token == null) {
    try {
      localStorage.removeItem(COMPLIANCE_AUTH_TOKEN_KEY);
    } catch {
      /* ignore */
    }
    Cookies.remove('panasia-portal-pro-token');
    return;
  }
  try {
    localStorage.setItem(COMPLIANCE_AUTH_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
  const expireTime = new Date(new Date().getTime() + TOKEN_COOKIE_EXPIRES_MS);
  Cookies.set('panasia-portal-pro-token', token, {
    expires: expireTime,
  });
}

export function getToken() {
  try {
    const fromLs = localStorage.getItem(COMPLIANCE_AUTH_TOKEN_KEY);
    if (fromLs) return fromLs;
  } catch {
    /* ignore */
  }
  const fromCookie = Cookies.get('panasia-portal-pro-token');
  if (fromCookie) {
    return fromCookie;
  }
  /** 仅纯前端 mock 账号模式 + dev 下无 Cookie 时回退，避免未登录请求把假 token 发到线上 */
  if (
    process.env.REACT_APP_USE_FRONTEND_MOCK_AUTH === 'true' &&
    process.env.REACT_APP_ENV === 'dev'
  ) {
    return 'd9034bc7ff6849954124fbddbee91ecc';
  }
  return undefined;
}

export const roundNum = (n: number, dp = 2) => new Big(n).round(dp).toNumber();

export function bankNo(num: string) {
  if (!num) return '';
  return num.replace(/\s/g, '').replace(/(\d{4})\d+(\d{4})$/, '$1 **** **** **** $2');
}

// 兼容 Chrome / Firefox / Safari
export const downloadFile = (url: string, filename?: string) => {
  request(url, {
    method: 'get',
    responseType: 'blob',
    prefix: '',
  }).then((blob) => {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename || link.href.split('/').pop() || '';
    link.click();
    URL.revokeObjectURL(link.href);
  });
};

export const openInNewTab = (href: string) => {
  Object.assign(document.createElement('a'), {
    target: '_blank',
    rel: 'noopener noreferrer',
    href: href,
  }).click();
};

export const getAvailableModes = () => {
  const modeStr = localStorage.getItem('availableModes');
  return modeStr?.split(',') ?? ['low'];
};

export const throttle = (fn, delay = 4600) => {
  let timer;
  return function () {
    if (timer) return;
    if (fn instanceof Function) {
      fn();
    }
    timer = setTimeout(() => {
      timer = null;
    }, delay);
  };
};

export const dataURItoBlob = function (dataURI: string) {
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
  else byteString = unescape(dataURI.split(',')[1]);
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};

export const isTrataoDomain = location.host.includes('tratao.com');

export const isTechDomain = location.host.includes('app.xhubs.tech');

export const isShowLogo = () => {
  const { host } = location;
  if (host.includes('fx.xcurrency.com')) {
    return false;
  }
  if (isTrataoDomain) {
    return false;
  }
  return true;
};

export const getFileName = (rawUrl: string) => {
  const noParamsUrl = rawUrl.split('?')[0];
  const lastSlashIndex = noParamsUrl.lastIndexOf('/');
  const fileName = noParamsUrl.slice(lastSlashIndex + 1);
  return fileName;
};

/**
 * 生成pdf预览图URL
 * @param url
 * @returns
 */
export const getPdfPreviewUrl = async function (url: string) {
  const documentRes = await pdfjs.getDocument({
    url,
    cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  }).promise;
  const page = await documentRes.getPage(1);
  const scale = 1.5;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;
  const renderContext = {
    canvasContext: context!,
    viewport: viewport,
  };
  let result = await page.render(renderContext).promise;
  result = canvas.toDataURL();

  return result;
};

/**
 * 过滤后端资源url的token
 */
export function removeQueryParamsFromUrl(url: string) {
  const urlParts = url.split('?');
  if (urlParts.length >= 2) {
    const baseUrl = urlParts.shift();
    return baseUrl;
  }
  return url;
}

/**
 * 判断对象是否为空
 */
export function isEmpty(obj: Record<string, any>) {
  let result = true;
  for (const key in obj) {
    if (key) result = false;
    break;
  }
  return result;
}

/**
 * 判断是否是不带token的私有资源
 * @param url
 * @returns
 */
export function isPrivateUrlWithoutQuery(url: string) {
  try {
    const parsedUrl = new URL(url);
    const hostCheck =
      parsedUrl.host.includes('private.tratao.com') ||
      parsedUrl.host.includes('private.xcurrency.com');
    const noQueryParams = parsedUrl.search === '';

    return hostCheck && noQueryParams;
  } catch (error) {
    console.error('Invalid URL:', error);
    return false;
  }
}

// 返回当前时区与该日期所在时区之间的时间差（以毫秒为单位）
function getTimezoneOffsetMS(date: Date) {
  const time = date.getTime();
  const utcTime = Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
  return time - utcTime;
}

// 计算当前时区与1899年12月30日时区之间的时间差（以毫秒为单位）
const importBugHotfixDiff = (function () {
  const baseDate = new Date(1899, 11, 30, 0, 0, 0);
  const dnThreshAsIs = (new Date().getTimezoneOffset() - baseDate.getTimezoneOffset()) * 60000;
  const dnThreshToBe = getTimezoneOffsetMS(new Date()) - getTimezoneOffsetMS(baseDate);
  return dnThreshAsIs - dnThreshToBe;
})();

// 修复日期的精度损失（需要注意传入的 Date 对象必须是已经转换为当前时区 Date 对象）
export function fixPrecisionLoss(date: Date) {
  return new Date(date.getTime() - importBugHotfixDiff);
}

export function rgbaToHsva(inR: number, inG: number, inB: number, inA = 1) {
  const r = inR / 255; // 将 RGB 转换为 [0, 1] 区间
  const g = inG / 255;
  const b = inB / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0,
    s = 0;
  const v = max;

  if (delta !== 0) {
    // 计算 H (色调)
    if (max === r) {
      h = (g - b) / delta + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h *= 60;

    // 计算 S (饱和度)
    s = max === 0 ? 0 : delta / max;
  }

  return { h, s, v, a: inA };
}

export function hexToRgba(inHex: string) {
  // 去掉前面的 `#` 号（如果有的话）
  let hex = inHex.replace(/^#/, '');

  // 处理短格式 (#RGB -> #RRGGBB, #RGBA -> #RRGGBBAA)
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // 解析 RGB 值
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // 如果有透明度部分，解析为 [0, 1]，否则默认给 1
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

  return { r, g, b, a };
}

export function hexToHsva(hex: string) {
  const { r, g, b, a } = hexToRgba(hex);
  return rgbaToHsva(r, g, b, a);
}

export async function downloadPayoutVoucher(pdfData: any, fileName: string) {
  try {
    const resp = await request('/api/client/xtool/pdf/transaction', {
      method: 'post',
      data: pdfData,
    });

    if (resp.status === 1 || resp.status === '1') {
      try {
        const response = await fetch(resp.data);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.pdf`);

        document.body.appendChild(link);
        link.click();
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url); // Clean up
      } catch (error) {
        throw new Error('Download error');
      }
    } else {
      throw new Error(resp.message);
    }
  } catch (error) {
    throw new Error('Download error');
  }
}

export async function downloadPayoutVoucherV2(
  pdfData: DownloadPayoutVoucherV2Params,
  fileName: string,
) {
  try {
    const resp = await request('/api/client/xtool/pdf/v2/transaction', {
      method: 'post',
      data: pdfData,
    });

    if (resp.status === 1 || resp.status === '1') {
      try {
        const response = await fetch(resp.data);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName}.pdf`);

        document.body.appendChild(link);
        link.click();
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
        window.URL.revokeObjectURL(url); // Clean up
      } catch (error) {
        throw new Error('Download error');
      }
    } else {
      throw new Error(resp.message);
    }
  } catch (e) {}
}

export function isAllChinese(str: string) {
  const chineseRegex = /^[\u4e00-\u9fa5]+$/;
  return chineseRegex.test(str);
}

/**
 * 根据身份证号获取生日
 * @param idCard 身份证号
 * @returns 生日字符串，格式为YYYY-MM-DD，如果身份证号无效则返回空字符串
 */
export function getBirthdayFromIdCard(idCard: string): string {
  if (!idCard || typeof idCard !== 'string') {
    return '';
  }

  // 去除空格和特殊字符
  const cleanIdCard = idCard.replace(/\s/g, '');

  // 验证身份证号格式
  if (!/^\d{17}[\dXx]$/.test(cleanIdCard) && !/^\d{15}$/.test(cleanIdCard)) {
    return '';
  }

  let year: string;
  let month: string;
  let day: string;

  if (cleanIdCard.length === 18) {
    // 18位身份证号
    year = cleanIdCard.substring(6, 10);
    month = cleanIdCard.substring(10, 12);
    day = cleanIdCard.substring(12, 14);
  } else {
    // 15位身份证号
    year = '19' + cleanIdCard.substring(6, 8);
    month = cleanIdCard.substring(8, 10);
    day = cleanIdCard.substring(10, 12);
  }

  // 验证日期有效性
  const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  if (
    birthDate.getFullYear() !== parseInt(year) ||
    birthDate.getMonth() !== parseInt(month) - 1 ||
    birthDate.getDate() !== parseInt(day)
  ) {
    return '';
  }

  // 返回格式化的日期字符串
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}
